// src/pages/Inventory.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import CarCard from '../components/CarCard';

// --- DATA: World Currencies (Must match AdminDashboard) ---
const CURRENCIES = [
  { code: "$", label: "$ (USD)", maxLimit: 500000, step: 1000 },
  { code: "₵", label: "₵ (GHS)", maxLimit: 5000000, step: 10000 },
  { code: "₦", label: "₦ (NGN)", maxLimit: 500000000, step: 500000 },
  { code: "£", label: "£ (GBP)", maxLimit: 400000, step: 1000 },
  { code: "€", label: "€ (EUR)", maxLimit: 450000, step: 1000 },
  { code: "C$", label: "C$ (CAD)", maxLimit: 600000, step: 1000 },
  { code: "A$", label: "A$ (AUD)", maxLimit: 700000, step: 1000 },
  { code: "R", label: "R (ZAR)", maxLimit: 8000000, step: 10000 },
  { code: "AED", label: "AED (Dirham)", maxLimit: 2000000, step: 5000 },
  // Fallback default
  { code: "OTHER", label: "Other", maxLimit: 1000000, step: 1000 }
];

// --- Reusable Component for Scroll Animations ---
const AnimatedSection = ({ children, direction = 'up', delay = 0, style = {} }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target); 
        }
      });
    }, { threshold: 0.1 });

    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  let initialTransform = 'translateY(20px)';
  if (direction === 'left') initialTransform = 'translateX(-30px)';
  if (direction === 'right') initialTransform = 'translateX(30px)';

  return (
    <div
      ref={domRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0, 0)' : initialTransform,
        transition: `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s`,
        width: '100%', 
        ...style
      }}
    >
      {children}
    </div>
  );
};

const Inventory = () => {
  // State for filters
  const [selectedCurrency, setSelectedCurrency] = useState("$"); // Default to USD
  const [filters, setFilters] = useState({ make: '', maxPrice: 500000 });
  const [sort, setSort] = useState('price-asc');
  
  // Data State
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get current currency settings
  const currentCurrencySettings = CURRENCIES.find(c => c.code === selectedCurrency) || CURRENCIES[0];

  // --- Fetch Data ---
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "cars"));
        const carsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCars(carsData);
      } catch (error) {
        console.error("Error fetching inventory:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  // Update max price slider when currency changes to avoid "stuck" values
  // FIX: Added currentCurrencySettings.maxLimit to the dependency array
  useEffect(() => {
      setFilters(prev => ({ ...prev, maxPrice: currentCurrencySettings.maxLimit }));
  }, [currentCurrencySettings.maxLimit]);

  const uniqueMakes = [...new Set(cars.map(car => car.make))].sort();

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // Filter Logic
  const filteredCars = cars.filter(car => {
    const carCurrency = car.currency || '$'; // Default to $ if missing
    
    // Filter 1: Must match selected currency
    if (carCurrency !== selectedCurrency) return false;

    // Filter 2: Make
    if (filters.make && car.make !== filters.make) return false;

    // Filter 3: Price
    return car.price <= parseInt(filters.maxPrice);
  }).sort((a, b) => {
      if (sort === 'price-asc') return a.price - b.price;
      if (sort === 'price-desc') return b.price - a.price;
      return 0;
  });

  return (
    <div className="container" style={{padding: '40px 20px', overflowX: 'hidden'}}>
      
      <AnimatedSection direction="up">
        <h1 style={{marginBottom: '20px'}}>Our Dealership</h1>
      </AnimatedSection>
      
      <div style={{display: 'flex', gap: '30px', flexWrap: 'wrap'}}>
        
        {/* Sidebar Filters */}
        <aside style={{flex: '1', minWidth: '250px', maxWidth: '300px'}}>
            <AnimatedSection direction="left" delay={0.2}>
                <div style={{background: 'white', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', position: 'sticky', top: '20px'}}>
                    <h3>Filter Results</h3>
                    <hr style={{margin: '10px 0', border: '0', borderTop: '1px solid #eee'}} />
                    
                    {/* Currency Selector */}
                    <div style={{marginBottom: '15px'}}>
                        <label style={{fontWeight: 'bold', display: 'block', marginBottom: '5px'}}>Currency</label>
                        <select 
                            value={selectedCurrency}
                            onChange={(e) => setSelectedCurrency(e.target.value)}
                            style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}
                        >
                            {CURRENCIES.map(c => (
                                <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <label style={{fontWeight: 'bold', display: 'block', marginBottom: '5px'}}>Make</label>
                        <select 
                            name="make" 
                            value={filters.make}
                            onChange={handleFilterChange}
                            style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}
                        >
                            <option value="">All Makes</option>
                            {uniqueMakes.map(make => (
                                <option key={make} value={make}>{make}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{marginBottom: '15px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '5px'}}>
                            <label style={{fontWeight: 'bold'}}>Max Price</label>
                            {/* Dynamic Currency Display */}
                            <span>{selectedCurrency}{parseInt(filters.maxPrice).toLocaleString()}</span>
                        </div>
                        
                        <input 
                            type="range" 
                            name="maxPrice" 
                            min="0" 
                            max={currentCurrencySettings.maxLimit} 
                            step={currentCurrencySettings.step} 
                            value={filters.maxPrice} 
                            onChange={handleFilterChange}
                            style={{
                                width: '100%', 
                                cursor: 'pointer',
                                accentColor: '#051337', 
                                height: '6px'
                            }} 
                        />
                    </div>
                    
                    <div style={{marginBottom: '15px'}}>
                        <label style={{fontWeight: 'bold', display: 'block', marginBottom: '5px'}}>Sort By</label>
                        <select 
                            onChange={(e) => setSort(e.target.value)}
                            style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc'}}
                        >
                            <option value="price-asc">Price: Low to High</option>
                            <option value="price-desc">Price: High to Low</option>
                        </select>
                    </div>
                </div>
            </AnimatedSection>
        </aside>

        {/* Results Grid */}
        <div style={{flex: '3', minWidth: '300px'}}>
            <div 
                style={{
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                    gap: '20px'
                }}
            >
                {loading ? (
                    <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '20px'}}>Loading dealership...</div>
                ) : filteredCars.length > 0 ? (
                    filteredCars.map((car, index) => (
                        <AnimatedSection key={car.id} direction="up" delay={(index % 3) * 0.1}>
                            <CarCard car={car} />
                        </AnimatedSection>
                    ))
                ) : (
                    <AnimatedSection direction="up" style={{gridColumn: '1/-1'}}>
                        <div style={{textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '8px'}}>
                            <h3>No vehicles found</h3>
                            <p>No cars found for <strong>{selectedCurrency}</strong> under this price.</p>
                            <p style={{fontSize: '0.9rem', color: '#666'}}>Try changing the currency filter or increasing the price range.</p>
                        </div>
                    </AnimatedSection>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default Inventory;